import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchLotsAdmin, publishLots, logoutAdmin } from '../lib/admin';

/* ───────────────────────────────────────────────
   EDITOR VISUAL DE LOTES — Santa Mónica
   Carga el plano, click para crear pin, arrastra para mover.
─────────────────────────────────────────────── */

const STORAGE_KEY = 'sm_lotes_editor_v1';
const STATUS_COLORS = {
  disponible:     '#2e9d4f',  // verde
  apartado:       '#f4c430',  // amarillo
  vendido:        '#f6802c',  // naranja
  segunda_etapa:  '#9aa4ad',  // gris (segunda etapa)
};
const STATUS_LABELS = {
  disponible:    'Disponible',
  apartado:      'Apartado',
  vendido:       'Vendido',
  segunda_etapa: '2da etapa',
};

const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X',
               'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX',
               'XXI', 'XXII', 'XXIII', 'XXIV', 'XXV', 'XXVI', 'XXVII', 'XXVIII', 'XXIX', 'XXX'];
const toRoman = (n) => ROMAN[n] || String(n);
const DEFAULT_LOT = () => ({
  manzana: 1,
  numero: 1,
  modelo: 'Fernanda',
  terreno: 127.5,
  construccion: 127.5,
  precio: 2432600,
  estatus: 'disponible',
});

export default function LotesEditor() {
  const navigate = useNavigate();
  // pins: { id, x, y, ...DEFAULT_LOT }
  const [pins, setPins] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });
  const [serverState, setServerState] = useState('idle'); // 'idle' | 'loading' | 'publishing' | 'error'
  const [serverMsg, setServerMsg] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [draggingPinId, setDraggingPinId] = useState(null);
  const [showHelp, setShowHelp] = useState(true);
  const [filter, setFilter] = useState('all');
  const [pinScale, setPinScale] = useState(1);
  const [imgSize, setImgSize] = useState({ w: 2400, h: 1280 });
  const [locked, setLocked] = useState(false);
  const stageRef = useRef(null);
  const panStartRef = useRef(null);

  // Autosave
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pins));
  }, [pins]);

  const selected = pins.find(p => p.id === selectedId);

  /* ───── COORDS HELPERS ───── */
  const pctFromEvent = useCallback((ev) => {
    // Compute coordinates relative to the CANVAS (= image bounding box), not the stage.
    const canvas = stageRef.current?.querySelector('.ed-canvas');
    if (!canvas) return { x: 50, y: 50 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((ev.clientX - rect.left) / rect.width) * 100,
      y: ((ev.clientY - rect.top) / rect.height) * 100,
    };
  }, []);

  /* ───── STAGE CLICK = create pin ───── */
  const handleStageClick = (ev) => {
    if (draggingPinId || isPanning) return;
    if (ev.target.classList.contains('pin-dot') || ev.target.closest('.pin-dot')) return;
    if (locked) return; // No crear pins nuevos en modo bloqueado

    const { x, y } = pctFromEvent(ev);
    if (x < 0 || x > 100 || y < 0 || y > 100) return;

    // suggest next lot number for selected manzana (or 1)
    const baseMz = selected?.manzana || 1;
    const lotsInMz = pins.filter(p => p.manzana === baseMz);
    const maxN = lotsInMz.reduce((m, p) => Math.max(m, p.numero), 0);

    const newPin = {
      id: Date.now() + Math.random(),
      x, y,
      ...DEFAULT_LOT(),
      manzana: baseMz,
      numero: maxN + 1,
      estatus: selected?.estatus || 'disponible',
      modelo: selected?.modelo || 'Fernanda',
      terreno: selected?.terreno || 127.5,
      construccion: selected?.construccion || 127.5,
      precio: selected?.precio || 2432600,
    };
    setPins(prev => [...prev, newPin]);
    setSelectedId(newPin.id);
  };

  /* ───── PIN DRAG ───── */
  const handlePinMouseDown = (ev, id) => {
    ev.stopPropagation();
    if (ev.button === 2) return; // let context menu handle
    setDraggingPinId(id);
    setSelectedId(id);
  };
  const handleMouseMove = useCallback((ev) => {
    if (draggingPinId) {
      const { x, y } = pctFromEvent(ev);
      setPins(prev => prev.map(p => p.id === draggingPinId ? { ...p, x, y } : p));
    } else if (isPanning) {
      const dx = ev.clientX - panStartRef.current.x;
      const dy = ev.clientY - panStartRef.current.y;
      setPan({ x: panStartRef.current.panX + dx, y: panStartRef.current.panY + dy });
    }
  }, [draggingPinId, isPanning, pctFromEvent]);

  const endDrag = useCallback(() => {
    setDraggingPinId(null);
    setIsPanning(false);
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', endDrag);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', endDrag);
    };
  }, [handleMouseMove, endDrag]);

  /* ───── PIN CONTEXT MENU = delete ───── */
  const handlePinContext = (ev, id) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (window.confirm('¿Eliminar este lote?')) {
      setPins(prev => prev.filter(p => p.id !== id));
      if (selectedId === id) setSelectedId(null);
    }
  };

  /* ───── ZOOM ───── */
  const handleWheel = (ev) => {
    ev.preventDefault();
    const delta = ev.deltaY > 0 ? -0.1 : 0.1;
    setZoom(z => Math.max(0.5, Math.min(4, z + delta)));
  };

  /* ───── PAN — drag canvas with middle/space ───── */
  const handleStageMouseDown = (ev) => {
    if (ev.button === 1 || (ev.button === 0 && ev.shiftKey)) {
      ev.preventDefault();
      setIsPanning(true);
      panStartRef.current = {
        x: ev.clientX, y: ev.clientY,
        panX: pan.x, panY: pan.y,
      };
    }
  };

  /* ───── FORM UPDATE ───── */
  const updateSelected = (field, value) => {
    if (!selectedId) return;
    setPins(prev => prev.map(p => p.id === selectedId ? { ...p, [field]: value } : p));
  };

  /* ───── EXPORT / IMPORT ───── */
  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(pins, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `santa-monica-lotes-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const importJSON = (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const arr = JSON.parse(reader.result);
        if (Array.isArray(arr)) {
          setPins(arr.map(p => ({ ...DEFAULT_LOT(), ...p, id: p.id ?? Date.now() + Math.random() })));
          alert(`${arr.length} lotes importados`);
        }
      } catch (e) {
        alert('Archivo JSON inválido');
      }
    };
    reader.readAsText(file);
    ev.target.value = '';
  };

  /* ───── SERVER SYNC ───── */
  const loadFromServer = useCallback(async () => {
    setServerState('loading');
    setServerMsg('Cargando desde el servidor…');
    try {
      const data = await fetchLotsAdmin();
      const lots = (data?.lots || []).map(l => ({ ...DEFAULT_LOT(), ...l }));
      setPins(lots);
      setServerState('idle');
      setServerMsg(`✓ ${lots.length} lotes cargados del servidor`);
      setTimeout(() => setServerMsg(''), 3500);
    } catch (e) {
      setServerState('error');
      setServerMsg(`✗ Error al cargar: ${e.message}`);
    }
  }, []);

  const publishToServer = useCallback(async () => {
    if (!window.confirm(`¿Publicar ${pins.length} lotes al sitio público? Esto reemplaza la disponibilidad actual.`)) return;
    setServerState('publishing');
    setServerMsg('Publicando…');
    try {
      const data = await publishLots(pins);
      setServerState('idle');
      setServerMsg(`✓ Publicados ${data.saved} lotes. El sitio se actualiza ya.`);
      setTimeout(() => setServerMsg(''), 4500);
    } catch (e) {
      setServerState('error');
      setServerMsg(`✗ Error al publicar: ${e.message}`);
    }
  }, [pins]);

  const handleLogout = () => {
    logoutAdmin();
    navigate('/admin/login');
  };

  // Auto-load from server on first mount if local storage is empty
  useEffect(() => {
    if (pins.length === 0) {
      loadFromServer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ───── COUNTS ───── */
  const counts = {
    total: pins.length,
    disponible: pins.filter(p => p.estatus === 'disponible').length,
    apartado: pins.filter(p => p.estatus === 'apartado').length,
    vendido: pins.filter(p => p.estatus === 'vendido').length,
    segunda_etapa: pins.filter(p => p.estatus === 'segunda_etapa').length,
  };

  /* ───── KEYBOARD SHORTCUTS ───── */
  useEffect(() => {
    const onKey = (ev) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(ev.target.tagName)) return;
      if (ev.key === 'Delete' || ev.key === 'Backspace') {
        if (selectedId && !ev.target.tagName.match(/INPUT|TEXTAREA/i)) {
          ev.preventDefault();
          if (window.confirm('¿Eliminar lote seleccionado?')) {
            setPins(prev => prev.filter(p => p.id !== selectedId));
            setSelectedId(null);
          }
        }
      }
      if (ev.key === 'Escape') setSelectedId(null);
      if (ev.key === '0') { setZoom(1); setPan({ x: 0, y: 0 }); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId]);

  const filteredPins = filter === 'all' ? pins : pins.filter(p => p.estatus === filter);

  return (
    <div className="ed-root" data-testid="lotes-editor">
      {/* SIDEBAR LEFT — top toolbar */}
      <div className="ed-topbar">
        <div className="ed-brand">
          <strong>Editor de Lotes</strong>
          <span className="ed-sub">Santa Mónica Residencial · {counts.total} lotes</span>
        </div>
        <div className="ed-counts">
          <span className="ed-count ed-disp"><i></i> {counts.disponible}</span>
          <span className="ed-count ed-apar"><i></i> {counts.apartado}</span>
          <span className="ed-count ed-vend"><i></i> {counts.vendido}</span>
          <span className="ed-count ed-seg"><i></i> {counts.segunda_etapa}</span>
        </div>
        <div className="ed-actions">
          <button onClick={() => { setZoom(z => Math.min(4, z + 0.2)); }} data-testid="zoom-in">＋</button>
          <button onClick={() => { setZoom(z => Math.max(0.5, z - 0.2)); }} data-testid="zoom-out">－</button>
          <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} data-testid="zoom-reset">Reset</button>
          <span className="ed-zoom-label">{Math.round(zoom * 100)}%</span>
          <span className="ed-divider" />
          <span className="ed-pin-size-label" title="Tamaño de los pins">Pin</span>
          <input
            type="range"
            min="0.5"
            max="2.5"
            step="0.1"
            value={pinScale}
            onChange={(e) => setPinScale(parseFloat(e.target.value))}
            className="ed-pin-slider"
            data-testid="pin-size"
          />
          <span className="ed-divider" />
          <button
            onClick={() => setLocked(l => !l)}
            data-testid="lock-toggle"
            style={locked ? { background: '#f6802c', borderColor: '#f6802c' } : undefined}
            title={locked ? 'Pins bloqueados — no se pueden mover ni crear nuevos' : 'Pins libres — click crea, drag mueve'}
          >
            {locked ? '🔒 Bloqueado' : '🔓 Libre'}
          </button>
          <span className="ed-divider" />
          <label className="ed-btn-import">
            Importar JSON
            <input type="file" accept="application/json" onChange={importJSON} style={{ display: 'none' }} data-testid="import-json" />
          </label>
          <button className="ed-btn-primary" onClick={exportJSON} data-testid="export-json">Exportar JSON</button>
          <span className="ed-divider" />
          <button
            onClick={loadFromServer}
            disabled={serverState === 'loading' || serverState === 'publishing'}
            data-testid="load-from-server"
            title="Cargar la versión actual publicada en el sitio"
          >
            ⇣ Cargar del servidor
          </button>
          <button
            className="ed-btn-primary"
            onClick={publishToServer}
            disabled={serverState === 'loading' || serverState === 'publishing' || pins.length === 0}
            data-testid="publish-to-server"
            title="Publica los cambios al sitio público inmediatamente"
            style={{ background: '#2e9d4f', borderColor: '#2e9d4f' }}
          >
            {serverState === 'publishing' ? 'Publicando…' : '⇡ Publicar al sitio'}
          </button>
          <span className="ed-divider" />
          <Link
            to="/admin/concierge"
            data-testid="link-concierge"
            title="Editor de conocimiento + conversaciones de Mónica AI"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              padding: '0.45rem 0.95rem',
              borderRadius: '8px',
              background: '#0a2236',
              border: '1px solid #0a2236',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.85rem',
              textDecoration: 'none',
            }}
          >
            ✨ Mónica AI
          </Link>
          <span className="ed-divider" />
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            title="Cerrar sesión de administrador"
          >
            Salir
          </button>
        </div>
      </div>
      {serverMsg && (
        <div
          style={{
            position: 'fixed',
            top: '70px',
            right: '20px',
            background: serverState === 'error' ? '#b91c1c' : '#1f3a5f',
            color: '#fff',
            padding: '0.7rem 1.1rem',
            borderRadius: '8px',
            fontSize: '0.85rem',
            fontWeight: 500,
            zIndex: 10000,
            boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
            maxWidth: '320px',
          }}
          data-testid="server-status-toast"
        >
          {serverMsg}
        </div>
      )}

      <div className="ed-main">
        {/* STAGE */}
        <div
          className="ed-stage"
          ref={stageRef}
          onClick={handleStageClick}
          onMouseDown={handleStageMouseDown}
          onWheel={handleWheel}
          onContextMenu={(e) => e.preventDefault()}
          style={{ cursor: isPanning ? 'grabbing' : 'crosshair' }}
          data-testid="ed-stage"
        >
          <div
            className="ed-canvas"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: '0 0',
              aspectRatio: `${imgSize.w} / ${imgSize.h}`,
            }}
          >
            <img
              src="/images/masterplan-cad.webp"
              alt="Plano CAD"
              className="ed-bg"
              draggable={false}
              onLoad={(e) => setImgSize({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
            />
            {pins.map(pin => {
              if (filter !== 'all' && pin.estatus !== filter) return null;
              const isSel = pin.id === selectedId;
              const color = STATUS_COLORS[pin.estatus] || '#f6802c';
              return (
                <button
                  key={pin.id}
                  type="button"
                  className={`pin-dot${isSel ? ' selected' : ''}`}
                  style={{
                    left: `${pin.x}%`,
                    top: `${pin.y}%`,
                    background: color,
                    width: `${1.6 * pinScale}%`,
                    aspectRatio: '1 / 1',
                    height: 'auto',
                    fontSize: `${0.45 * pinScale}vw`,
                    transform: 'translate(-50%, -50%)',
                  }}
                  onMouseDown={(e) => handlePinMouseDown(e, pin.id)}
                  onContextMenu={(e) => handlePinContext(e, pin.id)}
                  onClick={(e) => { e.stopPropagation(); setSelectedId(pin.id); }}
                  data-testid={`pin-${pin.id}`}
                >
                  <span className="pin-num">{pin.numero}</span>
                  {isSel && <span className="pin-ring" style={{ borderColor: color }} />}
                </button>
              );
            })}
          </div>

          {/* HELP CARD */}
          {showHelp && (
            <div className="ed-help">
              <button className="ed-help-close" onClick={() => setShowHelp(false)}>×</button>
              <h4>Cómo usar este editor</h4>
              <ul>
                <li><b>Click</b> sobre el plano → coloca un nuevo lote</li>
                <li><b>Arrastra</b> un pin → reposiciónalo</li>
                <li><b>Click derecho</b> sobre un pin → elimínalo</li>
                <li><b>Click</b> en pin → edita sus datos en el panel derecho →</li>
                <li><b>Scroll</b> → zoom in / out</li>
                <li><b>Shift + arrastrar</b> → mueve el plano</li>
                <li><b>Tecla 0</b> → reset zoom</li>
                <li>Tus datos se guardan automáticamente en este navegador. Cuando termines, presiona <b>Exportar JSON</b>.</li>
              </ul>
            </div>
          )}

          {/* FILTER OVERLAY */}
          <div className="ed-filter-overlay">
            <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>Todos ({counts.total})</button>
            <button className={filter === 'disponible' ? 'active' : ''} onClick={() => setFilter('disponible')}>
              <i style={{ background: STATUS_COLORS.disponible }} /> Disponibles ({counts.disponible})
            </button>
            <button className={filter === 'apartado' ? 'active' : ''} onClick={() => setFilter('apartado')}>
              <i style={{ background: STATUS_COLORS.apartado }} /> Apartados ({counts.apartado})
            </button>
            <button className={filter === 'vendido' ? 'active' : ''} onClick={() => setFilter('vendido')}>
              <i style={{ background: STATUS_COLORS.vendido }} /> Vendidos ({counts.vendido})
            </button>
            <button className={filter === 'segunda_etapa' ? 'active' : ''} onClick={() => setFilter('segunda_etapa')}>
              <i style={{ background: STATUS_COLORS.segunda_etapa }} /> 2da etapa ({counts.segunda_etapa})
            </button>
          </div>
        </div>

        {/* SIDEBAR RIGHT — Form */}
        <aside className="ed-sidebar">
          {selected ? (
            <div className="ed-form" data-testid="ed-form">
              <div className="ed-form-head">
                <div className="text-label" style={{ color: STATUS_COLORS[selected.estatus] }}>editando lote</div>
                <h3>Lote {selected.numero} · Mz {toRoman(selected.manzana)}</h3>
              </div>

              <div className="ed-field">
                <label>Estatus</label>
                <div className="ed-status-row">
                  {Object.keys(STATUS_LABELS).map(st => (
                    <button
                      key={st}
                      type="button"
                      className={`ed-status-chip${selected.estatus === st ? ' active' : ''}`}
                      onClick={() => updateSelected('estatus', st)}
                      style={{ '--c': STATUS_COLORS[st] }}
                      data-testid={`status-${st}`}
                    >
                      <i style={{ background: STATUS_COLORS[st] }} />
                      {STATUS_LABELS[st]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="ed-grid-2">
                <div className="ed-field">
                  <label>Manzana <small style={{ color: '#f6802c', marginLeft: '0.4rem', fontWeight: 800 }}>{toRoman(selected.manzana)}</small></label>
                  <input
                    type="number"
                    min="1"
                    value={selected.manzana}
                    onChange={e => updateSelected('manzana', parseInt(e.target.value, 10) || 1)}
                    data-testid="field-manzana"
                  />
                </div>
                <div className="ed-field">
                  <label>Número de lote</label>
                  <input
                    type="number"
                    min="1"
                    value={selected.numero}
                    onChange={e => updateSelected('numero', parseInt(e.target.value, 10) || 1)}
                    data-testid="field-numero"
                  />
                </div>
              </div>

              <div className="ed-field">
                <label>Modelo</label>
                <div className="ed-status-row">
                  {['Fernanda', 'Mariana'].map(m => (
                    <button
                      key={m}
                      type="button"
                      className={`ed-status-chip${selected.modelo === m ? ' active' : ''}`}
                      onClick={() => updateSelected('modelo', m)}
                      style={{ '--c': m === 'Fernanda' ? '#0d5c8a' : '#f6802c' }}
                      data-testid={`model-${m}`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              <div className="ed-grid-2">
                <div className="ed-field">
                  <label>Terreno (m²)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={selected.terreno}
                    onChange={e => updateSelected('terreno', parseFloat(e.target.value) || 0)}
                    data-testid="field-terreno"
                  />
                </div>
                <div className="ed-field">
                  <label>Construcción (m²)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={selected.construccion}
                    onChange={e => updateSelected('construccion', parseFloat(e.target.value) || 0)}
                    data-testid="field-construccion"
                  />
                </div>
              </div>

              <div className="ed-field">
                <label>Precio (MXN)</label>
                <input
                  type="number"
                  step="1000"
                  value={selected.precio}
                  onChange={e => updateSelected('precio', parseInt(e.target.value, 10) || 0)}
                  data-testid="field-precio"
                />
              </div>

              <div className="ed-coords">
                <span>x: {selected.x.toFixed(2)}%</span>
                <span>y: {selected.y.toFixed(2)}%</span>
              </div>

              <button
                className="ed-btn-danger"
                onClick={() => {
                  if (window.confirm('¿Eliminar este lote?')) {
                    setPins(prev => prev.filter(p => p.id !== selectedId));
                    setSelectedId(null);
                  }
                }}
                data-testid="delete-pin"
              >
                Eliminar lote
              </button>
            </div>
          ) : (
            <div className="ed-empty">
              <h4>Sin lote seleccionado</h4>
              <p>Haz <b>click sobre el plano</b> para crear un nuevo lote, o haz click sobre un pin existente para editarlo.</p>
              <div className="ed-stats-box">
                <div><span className="dot d" /> Disponibles<strong>{counts.disponible}</strong></div>
                <div><span className="dot a" /> Apartados<strong>{counts.apartado}</strong></div>
                <div><span className="dot v" /> Vendidos<strong>{counts.vendido}</strong></div>
                <div><span className="dot s" /> 2da etapa<strong>{counts.segunda_etapa}</strong></div>
                <div className="total"><span>Total</span><strong>{counts.total}</strong></div>
              </div>
              {pins.length > 0 && (
                <button
                  className="ed-btn-danger"
                  onClick={() => {
                    if (window.confirm(`¿Eliminar TODOS los ${pins.length} lotes? Esta acción no se puede deshacer.`)) {
                      setPins([]);
                    }
                  }}
                  data-testid="clear-all"
                >
                  Limpiar todo
                </button>
              )}
            </div>
          )}

          {/* LIST OF PINS */}
          {pins.length > 0 && (
            <div className="ed-list" data-testid="ed-list">
              <h5>Lotes capturados ({filteredPins.length})</h5>
              <div className="ed-list-scroll">
                {filteredPins
                  .sort((a, b) => a.manzana - b.manzana || a.numero - b.numero)
                  .map(p => (
                    <button
                      key={p.id}
                      className={`ed-list-item${p.id === selectedId ? ' selected' : ''}`}
                      onClick={() => setSelectedId(p.id)}
                    >
                      <i style={{ background: STATUS_COLORS[p.estatus] }} />
                      <span>Mz {toRoman(p.manzana)} · L {p.numero}</span>
                      <small>{p.modelo}</small>
                    </button>
                  ))}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
